/**
 * Staff-only usage log for /sp-quote.
 * Not linked in the navbar — open /sp-quote-logs while logged in.
 */

import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { SP_QUOTE_LOGS_COLLECTION } from './logSpQuoteUsage';
import { money, formatMinutes } from './pricing';

const Page = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 24px 16px 48px;
  color: white;
  font-family: ${props => props.theme.fonts.primary};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.25rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Meta = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--al-primary);
  padding: 8px 12px;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: pointer;

  &:hover {
    border-color: var(--al-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;

  th, td {
    text-align: left;
    padding: 10px 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    vertical-align: top;
  }

  th {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  td {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const Empty = styled.div`
  padding: 40px 0;
  color: rgba(255, 255, 255, 0.45);
`;

const Detail = styled.div`
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.75rem;
  margin-top: 4px;
`;

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds != null) return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatWhen(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleString();
}

function locationSummary(locations) {
  if (!locations) return '—';
  return ['front', 'back', 'other']
    .filter((key) => locations[key]?.enabled)
    .map((key) => {
      const loc = locations[key];
      if (loc.stitchTier) return `${key}:${loc.stitchTier}`;
      return `${key}:${loc.colors || 0}c`;
    })
    .join(', ') || 'none';
}

function SpQuoteLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const snap = await getDocs(
        query(
          collection(db, SP_QUOTE_LOGS_COLLECTION),
          orderBy('createdAt', 'desc'),
          limit(300)
        )
      );
      setLogs(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (err) {
      console.error('SP Quote logs load failed', err);
      setError(err.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const summary = useMemo(() => {
    const byType = {};
    logs.forEach((log) => {
      const key = log.decorationType || 'unknown';
      byType[key] = (byType[key] || 0) + 1;
    });
    return byType;
  }, [logs]);

  return (
    <Page>
      <Header>
        <Title>SP Quote usage</Title>
        <Meta>
          {logs.length} recent log{logs.length === 1 ? '' : 's'}
          {Object.keys(summary).length > 0 && (
            <> · {Object.entries(summary).map(([k, v]) => `${k}: ${v}`).join(' · ')}</>
          )}
        </Meta>
      </Header>

      <Toolbar>
        <Button type="button" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Toolbar>

      {error && <Empty>{error}</Empty>}
      {!error && loading && <Empty>Loading…</Empty>}
      {!error && !loading && logs.length === 0 && (
        <Empty>
          No settled quotes logged yet. Entries appear after someone leaves a quote unchanged for ~20 seconds on /sp-quote.
        </Empty>
      )}

      {!error && !loading && logs.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>When</th>
              <th>Job</th>
              <th>Floor → Ask</th>
              <th>Totals</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id}>
                <td>{formatWhen(log.createdAt)}</td>
                <td>
                  <div>
                    <strong>{log.decorationType}</strong> · {log.quantity} pcs
                  </div>
                  <Detail>
                    {(log.selectedStyle || log.garmentQuery || 'no style')} · COGS {money(log.cogs)}
                  </Detail>
                  <Detail>
                    {locationSummary(log.locations)}
                    {log.minutes != null ? ` · ${formatMinutes(log.minutes)}` : ''}
                  </Detail>
                </td>
                <td>
                  {money(log.floorPerPiece)} → {money(log.askPerPiece)}
                  <Detail>
                    merch {money(log.floorTotal)} → {money(log.askTotal)}
                  </Detail>
                </td>
                <td>
                  Grand {money(log.grandTotal)}
                  <Detail>
                    fee {money(log.jobFee)}
                    {log.taxExempt ? ' · tax exempt' : ` · tax ${money(log.taxAmount)}`}
                    {log.netIncome != null ? ` · net ${money(log.netIncome)}` : ''}
                  </Detail>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

export default SpQuoteLogsPage;
